import { Skeleton } from '@dixa/lib-shared-habla';
import { formatInstrumentNumber } from 'lib-analytics-ui';
import { DashboardSLAPolicy } from 'lib-dashboard-types';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';

import { DashboardSLAResponseTimeMetric } from 'app/views/layout/views/dashboard/components/metric/sla/slaResponseTimeMetric';

import { useChannelDashboardChannelContext } from '../../../../../../channelDashboardChannel.context';
import {
  ChannelDashboardMetric,
  ChannelDashboardMetricProps,
} from '../../../../../metric/channelDashboardMetric';

type ChannelDashboardSLAResponseTimeMetricProps = {
  /** The SLA policy which applies to this channel. */
  slaPolicy: DashboardSLAPolicy;
} & PropsWithClassName;

export const ChannelDashboardSLAResponseTimeMetric = memo(
  function ChannelDashboardSLAResponseTimeMetric({
    className,
    slaPolicy,
  }: ChannelDashboardSLAResponseTimeMetricProps): JSX.Element {
    const { metrics, isSkeleton } = useChannelDashboardChannelContext();

    const slaPolicyMetric = slaPolicy.metrics.find(
      ({ metric }) => metric === 'response_time',
    );

    const thresholdAlertTooltip =
      useMemo((): ChannelDashboardMetricProps['alertTooltip'] => {
        if (
          slaPolicyMetric?.evaluation_status !== 'Breached' ||
          !slaPolicyMetric.achieved_percentage
        ) {
          return null;
        }

        return {
          head: 'Service-level alert',
          content: (
            <>
              This channel is below the goal of{' '}
              <b>{slaPolicyMetric.percentage}%</b> conversations answered within{' '}
              <b>
                {formatInstrumentNumber(slaPolicyMetric.target, 'duration')}
              </b>
              .
            </>
          ),
        };
      }, [slaPolicyMetric]);

    return (
      <ChannelDashboardMetric
        className={className}
        title="Response time SLA"
        isSkeleton={isSkeleton}
        metricType="response_time"
        alertTooltip={thresholdAlertTooltip}
      >
        {isSkeleton ? (
          <Skeleton height="24px" width="72px" />
        ) : (
          <DashboardSLAResponseTimeMetric
            responseTime={metrics?.average_response_time}
            slaPolicyId={slaPolicy.id}
            slaPolicyMetric={slaPolicyMetric}
          />
        )}
      </ChannelDashboardMetric>
    );
  },
);
